import { createWorker } from 'tesseract.js';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/**
 * Convert PDF page to image canvas
 */
async function pdfPageToImage(pdfPage, scale = 2.0) {
  const viewport = pdfPage.getViewport({ scale });
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  canvas.height = viewport.height;
  canvas.width = viewport.width;

  const renderContext = {
    canvasContext: context,
    viewport: viewport
  };

  await pdfPage.render(renderContext).promise;
  return canvas;
}

/**
 * Extract table from planning PDF using OCR
 * Returns structured data with word positions
 */
export async function extractTableWithOCR(pdfFile) {
  console.log('🔍 Starting OCR extraction...');

  try {
    // 1. Load PDF
    const arrayBuffer = await pdfFile.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    console.log(`PDF loaded: ${pdf.numPages} pages`);

    // 2. Get the last page (where table usually is)
    const pageNum = pdf.numPages;
    const page = await pdf.getPage(pageNum);

    // 3. Convert page to high-res image
    const canvas = await pdfPageToImage(page, 3.0); // Higher scale for better OCR

    console.log(`Canvas created: ${canvas.width}x${canvas.height}`);

    // 4. Run OCR with Tesseract
    console.log('Running OCR...');
    const worker = await createWorker('fra'); // French language

    const { data } = await worker.recognize(canvas);

    console.log(`OCR complete: ${data.words.length} words detected`);
    console.log('OCR text:', data.text);

    await worker.terminate();

    // 5. Find table header
    const headerWords = data.words.filter(w => {
      const text = w.text.toUpperCase();
      return text.includes('ASTREINTE') ||
             text.includes('BASE') ||
             text.includes('SEABOB') ||
             text.includes('JETSKI') ||
             text.includes('BUREAU') ||
             text.includes('KITTS') ||
             text === 'OFF';
    });

    if (headerWords.length === 0) {
      console.warn('⚠️ No table header found');
      return null;
    }

    console.log('Found header words:', headerWords.map(w => w.text));

    // 6. Determine column positions from header
    const columns = detectColumns(headerWords);

    console.log('Detected columns:', columns);

    // 7. Find table Y position (below header)
    const headerY = Math.max(...headerWords.map(w => w.bbox.y1));

    // 8. Extract words in table area (below header, within reasonable bounds)
    const tableWords = data.words.filter(w => {
      const y = w.bbox.y0;
      return y > headerY && y < headerY + 300; // Assume table is within 300px below header
    });

    console.log(`Found ${tableWords.length} words in table area`);

    // 9. Assign words to columns based on X position
    const categorizedNames = assignWordsToColumns(tableWords, columns);

    console.log('Categorized names:', categorizedNames);

    return {
      columns,
      categories: categorizedNames,
      allWords: tableWords
    };

  } catch (error) {
    console.error('❌ OCR extraction error:', error);
    throw error;
  }
}

/**
 * Detect column positions from header words
 */
function detectColumns(headerWords) {
  const columns = [];

  // Sort header words by X position
  const sortedHeaders = [...headerWords].sort((a, b) => a.bbox.x0 - b.bbox.x0);

  for (let i = 0; i < sortedHeaders.length; i++) {
    const word = sortedHeaders[i];
    const text = word.text.toUpperCase();

    // Map header text to category name
    let categoryName = null;
    if (text.includes('ASTREINTE')) categoryName = 'ASTREINTE';
    else if (text === 'BASE') categoryName = 'BASE';
    else if (text.includes('SEABOB')) categoryName = 'SEABOB';
    else if (text.includes('JETSKI') || text.includes('JET')) categoryName = 'JETSKI';
    else if (text.includes('BUREAU')) categoryName = 'BUREAU';
    else if (text.includes('KITTS') || text.includes('ST')) categoryName = 'ST_KITTS';
    else if (text === 'OFF') categoryName = 'OFF';

    if (categoryName) {
      const xStart = word.bbox.x0;
      const xEnd = i < sortedHeaders.length - 1 ? sortedHeaders[i + 1].bbox.x0 : 10000;

      columns.push({
        name: categoryName,
        xStart,
        xEnd,
        headerText: word.text
      });
    }
  }

  return columns;
}

/**
 * Assign words to columns based on X position
 */
function assignWordsToColumns(words, columns) {
  const categories = {
    ASTREINTE: [],
    BASE: [],
    SEABOB: [],
    JETSKI: [],
    BUREAU: [],
    ST_KITTS: [],
    OFF: []
  };

  for (const word of words) {
    const wordX = word.bbox.x0 + (word.bbox.x1 - word.bbox.x0) / 2; // Center X
    const wordText = word.text.trim();

    // Skip very short words or numbers only
    if (wordText.length < 2 || /^\d+$/.test(wordText)) {
      continue;
    }

    // Skip header keywords
    const upperText = wordText.toUpperCase();
    if (upperText.includes('ASTREINTE') ||
        upperText.includes('SEABOB') ||
        upperText.includes('JETSKI') ||
        upperText.includes('BUREAU') ||
        upperText.includes('KITTS') ||
        upperText === 'BASE' ||
        upperText === 'OFF' ||
        upperText.includes('AIDES') ||
        upperText.includes('MISSIONS')) {
      continue;
    }

    // Find which column this word belongs to
    for (const col of columns) {
      if (wordX >= col.xStart && wordX < col.xEnd) {
        // Clean the word text
        let cleanedText = cleanWordText(wordText);

        if (cleanedText && cleanedText.length > 1) {
          categories[col.name].push(cleanedText);
        }
        break;
      }
    }
  }

  // Remove duplicates
  for (const cat in categories) {
    categories[cat] = [...new Set(categories[cat])];
  }

  return categories;
}

/**
 * Clean word text (remove AM/PM, time prefixes, etc.)
 */
function cleanWordText(text) {
  let cleaned = text.trim();

  // Remove time prefixes (7H, 8H, etc.)
  cleaned = cleaned.replace(/^\d+H?\s*/i, '');

  // Remove AM/PM
  cleaned = cleaned.replace(/\s*(AM|PM)\s*$/i, '');

  // Remove + prefix
  cleaned = cleaned.replace(/^\+\s*/, '');

  // Remove numbers at the end
  cleaned = cleaned.replace(/\s*\d+\s*$/, '');

  return cleaned.trim();
}

export default {
  extractTableWithOCR
};
